/**
 * useJsonSchemaFields - Decompose a CRM JSON Schema into renderable field descriptors.
 *
 * CRM JSON Schemas follow a flat-object structure where each property is either:
 * - An entity reference (`x-entity-type`): renders as an association select
 * - A field type reference (`x-field-type`): renders as a standard PropertyInput
 *
 * Properties can be singular (type: "string") or multi-value (type: "array").
 * This hook parses the schema and returns descriptors that drive rendering
 * in JsonPropertyFields.
 */

import { useMemo } from 'react';

/**
 * Descriptor for a single field derived from a JSON Schema property.
 */
export interface JsonSchemaField {
  /** Property key in the JSON object (e.g. "assignable_users") */
  key: string;
  /** Human-readable label derived from title, description, or key */
  label: string;
  /** Whether this field references an entity type or a field type */
  refType: 'entity' | 'field';
  /** Entity type name when refType is 'entity' (e.g. "ahoy_user") */
  entityTypeName?: string;
  /** Field type name when refType is 'field' (e.g. "number", "text") */
  fieldTypeName?: string;
  /** Whether the field accepts multiple values (array type in schema) */
  isMulti: boolean;
  /** Whether the field accepts null values */
  isNullable: boolean;
  /** Whether the field is required per the schema's required array */
  isRequired: boolean;
  /** Optional description from the schema property */
  description?: string;
}

/**
 * Check if a JSON Schema type declaration represents a string (plain or nullable).
 */
function isStringType(typeVal: unknown): {
  isString: boolean;
  isNullable: boolean;
} {
  if (typeVal === 'string') {
    return { isString: true, isNullable: false };
  }
  if (Array.isArray(typeVal)) {
    const sorted = [...typeVal].sort();
    if (sorted.length === 2 && sorted[0] === 'null' && sorted[1] === 'string') {
      return { isString: true, isNullable: true };
    }
  }
  return { isString: false, isNullable: false };
}

/**
 * Convert a property key to a human-readable label.
 * "assignable_users" → "Assignable users", "max_contacts_per_rep" → "Max contacts per rep"
 */
function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  if (!words) {
    return key;
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Extract field descriptor from a single JSON Schema property definition.
 * Returns null if the property cannot be mapped to a renderable field.
 */
function parseSchemaProperty(
  key: string,
  propSchema: Record<string, unknown>,
  requiredSet: Set<string>
): JsonSchemaField | null {
  const propType = propSchema.type;
  const title = propSchema.title as string | undefined;
  const description = propSchema.description as string | undefined;
  const label = title || humanizeKey(key);

  // Case 1: string leaf (plain or nullable)
  const { isString, isNullable } = isStringType(propType);
  if (isString) {
    const entityType = propSchema['x-entity-type'] as string | undefined;
    const fieldType = propSchema['x-field-type'] as string | undefined;

    if (entityType) {
      return {
        key,
        label,
        refType: 'entity',
        entityTypeName: entityType,
        isMulti: false,
        isNullable,
        isRequired: requiredSet.has(key),
        description,
      };
    }
    if (fieldType) {
      return {
        key,
        label,
        refType: 'field',
        fieldTypeName: fieldType,
        isMulti: false,
        isNullable,
        isRequired: requiredSet.has(key),
        description,
      };
    }
    return null;
  }

  // Case 2: array with string items
  if (propType === 'array') {
    const items = propSchema.items as Record<string, unknown> | undefined;
    if (!items || typeof items !== 'object') {
      return null;
    }
    const { isString: itemIsString } = isStringType(items.type);
    if (!itemIsString) {
      return null;
    }

    const entityType = items['x-entity-type'] as string | undefined;
    const fieldType = items['x-field-type'] as string | undefined;

    if (entityType) {
      return {
        key,
        label,
        refType: 'entity',
        entityTypeName: entityType,
        isMulti: true,
        isNullable: false,
        isRequired: requiredSet.has(key),
        description,
      };
    }
    if (fieldType) {
      return {
        key,
        label,
        refType: 'field',
        fieldTypeName: fieldType,
        isMulti: true,
        isNullable: false,
        isRequired: requiredSet.has(key),
        description,
      };
    }
    return null;
  }

  return null;
}

/**
 * Parse a CRM JSON Schema into an ordered list of renderable field descriptors.
 *
 * Pure function — no React dependencies.  Used by the hook below and by
 * non-hook call sites (e.g. timeline sub-item generation).
 *
 * @param schema - The JSON Schema object (must be type: "object" with properties)
 * @returns Array of field descriptors, one per schema property
 */
export function parseJsonSchemaFields(
  schema: Record<string, unknown> | null | undefined
): JsonSchemaField[] {
  if (!schema || typeof schema !== 'object') {
    return [];
  }

  const properties = schema.properties as
    | Record<string, Record<string, unknown>>
    | undefined;
  if (!properties || typeof properties !== 'object') {
    return [];
  }

  const requiredArray = Array.isArray(schema.required)
    ? (schema.required as string[])
    : [];
  const requiredSet = new Set(requiredArray);

  const fields: JsonSchemaField[] = [];
  for (const [key, propSchema] of Object.entries(properties)) {
    if (!propSchema || typeof propSchema !== 'object') {
      continue;
    }
    const field = parseSchemaProperty(key, propSchema, requiredSet);
    if (field) {
      fields.push(field);
    }
  }

  return fields;
}

/**
 * React hook wrapper around {@link parseJsonSchemaFields}.
 *
 * Memoises the result so it's stable across re-renders.
 */
export function useJsonSchemaFields(
  schema: Record<string, unknown> | null | undefined
): JsonSchemaField[] {
  return useMemo(() => parseJsonSchemaFields(schema), [schema]);
}
